import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useBranding } from '../../../context/branding/branding.context';
import { useAuth } from '../../../context/auth';
import { DEFAULT_BRAND_COLORS, DEFAULT_GLOBAL_COLORS } from '../../../utils/buildTheme';
import { updateBrandingService } from '../../../services/branding.service';
import { isHttpUrl } from '../../../utils/brandingAssets';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES } from '../../../utils/constants';
import DefaultLogo from '../../../assets/images/PoweredByDQ_Default.png';
import { toBrandingLogoSrc } from '../../../utils/toBrandingLogoSrc';

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const CORE_COLOR_FIELDS = [
    { key: 'primary', label: 'Primary', help: 'Buttons, highlights and active states.' },
    { key: 'secondary', label: 'Secondary', help: 'Secondary buttons and accents.' },
    { key: 'text', label: 'Text', help: 'Default heading / body text color.' },
    { key: 'background', label: 'Background', help: 'App background color.' },
    { key: 'section_bg', label: 'Section background', help: 'Cards / section panels background.' },
    { key: 'primary_title', label: 'Primary title', help: 'Main heading color.' },
    { key: 'secondary_title', label: 'Secondary title', help: 'Sub-heading color.' },
];

const EXTRA_COLOR_FIELDS = [
    { key: 'global_1', label: 'Global color 1', help: 'Default: My Workspace button (#FF6900).' },
    { key: 'global_2', label: 'Global color 2', help: 'Default: Run an Assessment button (#7B8FC7).' },
    { key: 'global_3', label: 'Global color 3', help: 'Default: CPC Index button (#0097B2).' },
];

const COLOR_FIELDS = [...CORE_COLOR_FIELDS, ...EXTRA_COLOR_FIELDS];

const FONT_FIELDS = [
    { key: 'primary', label: 'Primary font', help: 'Upload a font file or paste a font URL.' },
    { key: 'secondary', label: 'Secondary font', help: 'Optional — upload a file or paste a URL.' },
];

const COLOR_DEFAULTS = {
    primary: DEFAULT_BRAND_COLORS.primary,
    secondary: DEFAULT_BRAND_COLORS.secondary,
    text: DEFAULT_BRAND_COLORS.text,
    background: DEFAULT_BRAND_COLORS.background,
    section_bg: DEFAULT_BRAND_COLORS.background,
    primary_title: DEFAULT_BRAND_COLORS.text,
    secondary_title: DEFAULT_BRAND_COLORS.secondary,
    global_1: DEFAULT_GLOBAL_COLORS.global_1,
    global_2: DEFAULT_GLOBAL_COLORS.global_2,
    global_3: DEFAULT_GLOBAL_COLORS.global_3,
};

const EMPTY_COLORS = COLOR_FIELDS.reduce((acc, f) => { acc[f.key] = ''; return acc; }, {});
const EMPTY_FONTS = { primary: '', secondary: '' };
const EMPTY_FONT_UPLOADS = { primary: null, secondary: null };

const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const fileToBase64Payload = async (file) => {
    const dataUrl = await fileToDataUrl(file);
    const mime_type = file.type || 'application/octet-stream';
    return { file_base64: dataUrl, mime_type, name: file.name };
};

const cardSx = {
    backgroundColor: 'var(--brand-surface, #fff)',
    border: '1px solid var(--brand-border, #E5E7EB)',
    borderRadius: '14px',
    p: 3,
};

// Primary actions use brand primary (matches "Primary" color help text on this tab).
const primaryBtnSx = {
    textTransform: 'none',
    backgroundColor: 'var(--brand-primary, #FF6900)',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#9CA3AF' },
};

const ColorGrid = ({ fields, colors, readOnly, onChange }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3, rowGap: 2.5 }}>
        {fields.map(({ key, label, help }) => {
            const val = colors[key];
            const swatch = (val && HEX.test(val.trim())) ? val.trim() : COLOR_DEFAULTS[key];
            return (
                <Box key={key}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 0.75 }}>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: readOnly ? 0.85 : 1 }}>
                        <input
                            type="color" value={swatch} disabled={readOnly}
                            onChange={(e) => onChange(key, e.target.value)}
                            style={{ width: 44, height: 40, border: '1px solid #E5E7EB', borderRadius: 8, padding: 2, background: '#fff', cursor: readOnly ? 'default' : 'pointer' }}
                            aria-label={`${label} color`}
                        />
                        <TextField
                            size="small" placeholder={COLOR_DEFAULTS[key]} value={val} disabled={readOnly}
                            onChange={(e) => onChange(key, e.target.value)} sx={{ width: 140 }}
                        />
                        {val && !readOnly ? (
                            <Button size="small" onClick={() => onChange(key, '')} sx={{ textTransform: 'none', color: '#6B7280' }}>Default</Button>
                        ) : null}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mt: 0.5 }}>{help}</Typography>
                </Box>
            );
        })}
    </Box>
);

const AppearanceTab = () => {
    const { branding, setBranding, userCanEditBranding } = useBranding();
    const { authUser, setAuthUser } = useAuth();
    const readOnly = !userCanEditBranding;
    const fileInputRef = useRef(null);
    const cssFileInputRef = useRef(null);
    const fontFileInputRefs = useRef({ primary: null, secondary: null });
    const wsId = authUser && authUser.dq_insider_id;

    const applyBranding = (b) => {
        if (!b) return;
        setBranding(b, wsId);
        // Persist the new branding into the auth user as well. On refresh the app
        // re-applies branding from authUser.branding (restored from localStorage at
        // boot), which still holds the LOGIN-time value and would otherwise override
        // the freshly-saved branding — making the change disappear on reload. Merge
        // only the branding field so no other session data is disturbed (same
        // principle as mergeOnboardingRefreshIntoAuthUser).
        if (authUser && setAuthUser) {
            setAuthUser({ ...authUser, branding: b });
        }
    };

    const [colors, setColors] = useState(EMPTY_COLORS);
    const [fontLinks, setFontLinks] = useState(EMPTY_FONTS);
    const [fontUploads, setFontUploads] = useState(EMPTY_FONT_UPLOADS);
    const [stylesheetLink, setStylesheetLink] = useState('');
    const [stylesheetUpload, setStylesheetUpload] = useState(null);
    const [stylesheetPreview, setStylesheetPreview] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [removingLogo, setRemovingLogo] = useState(false);
    const [logoPreviewFailed, setLogoPreviewFailed] = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const dirtyRef = useRef(false);

    const loadStylesheetPreview = async (url) => {
        if (!url) { setStylesheetPreview(''); return; }
        try {
            const res = await fetch(url);
            if (res.ok) setStylesheetPreview(await res.text());
            else setStylesheetPreview('(Could not load preview)');
        } catch {
            setStylesheetPreview('(Could not load preview)');
        }
    };

    useEffect(() => {
        if (dirtyRef.current) return;
        const c = (branding && branding.colors) || {};
        const f = (branding && branding.fonts) || {};
        setColors(COLOR_FIELDS.reduce((acc, fld) => { acc[fld.key] = c[fld.key] || ''; return acc; }, {}));
        setFontLinks({ primary: f.primary || '', secondary: f.secondary || '' });
        setFontUploads(EMPTY_FONT_UPLOADS);
        const sheet = branding?.custom_stylesheet || '';
        setStylesheetLink(sheet);
        setStylesheetUpload(null);
        loadStylesheetPreview(sheet);
        setLogoPreviewFailed(false);
    }, [branding]);

    const markDirty = () => { dirtyRef.current = true; };
    const handleColorChange = (key, value) => { markDirty(); setColors((prev) => ({ ...prev, [key]: value })); };
    const handleFontLinkChange = (key, value) => {
        markDirty();
        setFontLinks((prev) => ({ ...prev, [key]: value }));
        setFontUploads((prev) => ({ ...prev, [key]: null }));
    };

    const handleFontFile = async (key, e) => {
        const file = e.target.files && e.target.files[0];
        if (fontFileInputRefs.current[key]) fontFileInputRefs.current[key].value = '';
        if (!file) return;
        try {
            const payload = await fileToBase64Payload(file);
            markDirty();
            setFontUploads((prev) => ({ ...prev, [key]: payload }));
            setFontLinks((prev) => ({ ...prev, [key]: '' }));
        } catch {
            toast(TOAST_TYPES.WARN, 'Appearance', 'Could not read that font file.');
        }
    };

    const handleStylesheetFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (cssFileInputRef.current) cssFileInputRef.current.value = '';
        if (!file) return;
        try {
            const payload = await fileToBase64Payload(file);
            markDirty();
            setStylesheetUpload(payload);
            setStylesheetLink('');
            setStylesheetPreview(await file.text());
        } catch {
            toast(TOAST_TYPES.WARN, 'Appearance', 'Could not read that CSS file.');
        }
    };

    const buildPayload = () => {
        const payloadColors = {};
        for (const { key, label } of COLOR_FIELDS) {
            const v = String(colors[key] || '').trim();
            if (!v) { payloadColors[key] = null; continue; }
            if (!HEX.test(v)) {
                toast(TOAST_TYPES.WARN, 'Appearance', `Invalid ${label} color. Use a hex value like #1A2B3C.`);
                return null;
            }
            payloadColors[key] = v;
        }

        const payloadFonts = {};
        const payloadFontUploads = {};
        for (const { key } of FONT_FIELDS) {
            if (fontUploads[key]) {
                payloadFontUploads[key] = {
                    file_base64: fontUploads[key].file_base64,
                    mime_type: fontUploads[key].mime_type,
                };
            } else {
                const link = String(fontLinks[key] || '').trim();
                if (link && !isHttpUrl(link)) {
                    toast(TOAST_TYPES.WARN, 'Appearance', 'Font link must be a valid URL (https://…).');
                    return null;
                }
                payloadFonts[key] = link || null;
            }
        }

        const payload = {
            colors: payloadColors,
            fonts: Object.keys(payloadFonts).length ? payloadFonts : undefined,
            font_uploads: Object.keys(payloadFontUploads).length ? payloadFontUploads : undefined,
        };

        if (stylesheetUpload) {
            payload.stylesheet_upload = {
                file_base64: stylesheetUpload.file_base64,
                mime_type: stylesheetUpload.mime_type,
            };
        } else {
            const link = String(stylesheetLink || '').trim();
            if (link && !isHttpUrl(link)) {
                toast(TOAST_TYPES.WARN, 'Appearance', 'Stylesheet link must be a valid URL (https://…).');
                return null;
            }
            payload.custom_stylesheet = link || null;
        }

        return payload;
    };

    const handleSave = async () => {
        const payload = buildPayload();
        if (!payload) return;
        setSaving(true);
        try {
            const res = await updateBrandingService(payload);
            if (res && res.data && res.data.branding) {
                dirtyRef.current = false;
                applyBranding(res.data.branding);
                toast(TOAST_TYPES.SUCCESS, 'Appearance', 'Appearance updated.');
            }
        } catch {
            /* toasted upstream */
        } finally {
            setSaving(false);
        }
    };

    const handleResetConfirm = async () => {
        setResetDialogOpen(false);
        setSaving(true);
        try {
            const resetColors = COLOR_FIELDS.reduce((acc, f) => { acc[f.key] = null; return acc; }, {});
            const res = await updateBrandingService({
                colors: resetColors,
                fonts: { primary: null, secondary: null },
                custom_stylesheet: null,
            });
            dirtyRef.current = false;
            if (res && res.data && res.data.branding) applyBranding(res.data.branding);
            toast(TOAST_TYPES.SUCCESS, 'Appearance', 'Appearance reset to default.');
        } catch {
            /* toasted upstream */
        } finally {
            setSaving(false);
        }
    };

    const handleLogoPick = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file) return;
        if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
            toast(TOAST_TYPES.WARN, 'Appearance', 'Use a PNG, JPG, SVG or WEBP image.');
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            toast(TOAST_TYPES.WARN, 'Appearance', 'Logo must be under 2 MB.');
            return;
        }
        setUploadingLogo(true);
        try {
            const dataUrl = await fileToDataUrl(file);
            const res = await updateBrandingService({ image_base64: dataUrl, mime_type: file.type });
            if (res && res.data && res.data.branding) {
                applyBranding(res.data.branding);
                toast(TOAST_TYPES.SUCCESS, 'Appearance', 'Logo updated.');
            }
        } catch {
            /* toasted upstream */
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleRemoveLogo = async () => {
        setRemovingLogo(true);
        try {
            const res = await updateBrandingService({ logo_url: null });
            if (res && res.data && res.data.branding) applyBranding(res.data.branding);
            toast(TOAST_TYPES.SUCCESS, 'Appearance', 'Logo removed. Default logo restored.');
        } catch {
            /* toasted upstream */
        } finally {
            setRemovingLogo(false);
        }
    };

    const hasCustomLogo = !!(branding && branding.logo_url);
    const previewSrc = (hasCustomLogo && !logoPreviewFailed)
        ? toBrandingLogoSrc(branding.logo_url)
        : DefaultLogo;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {readOnly && (
                <Box sx={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '10px', px: 2, py: 1.5 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#4B5563' }}>
                        Your administrator manages workspace branding. You can preview settings here but cannot make changes.
                    </Typography>
                </Box>
            )}

            {/* Logo */}
            <Box sx={cardSx}>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.5 }}>Workspace logo</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'var(--brand-text-muted, #6B7280)', mb: 2 }}>
                    Shown in the sidebar across your workspace. Transparent PNG or SVG works best.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{
                        height: 72, minWidth: 180, px: 2, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', border: '1px dashed #D1D5DB', borderRadius: '10px', backgroundColor: '#FAFAFA',
                    }}>
                        <img
                            key={branding && branding.logo_url}
                            src={previewSrc}
                            alt="Logo preview"
                            onError={() => setLogoPreviewFailed(true)}
                            style={{ height: 48, width: 'auto', maxWidth: 240, objectFit: 'contain', display: 'block' }}
                        />
                    </Box>
                    {!readOnly && (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained" disableElevation disabled={uploadingLogo}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                startIcon={uploadingLogo ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                                sx={primaryBtnSx}
                            >
                                {uploadingLogo ? 'Uploading…' : (hasCustomLogo ? 'Replace logo' : 'Upload logo')}
                            </Button>
                            {hasCustomLogo && (
                                <Button variant="outlined" disabled={removingLogo} onClick={handleRemoveLogo} sx={{ textTransform: 'none' }}>
                                    {removingLogo ? 'Removing…' : 'Remove'}
                                </Button>
                            )}
                            <input
                                ref={fileInputRef} type="file"
                                accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
                                style={{ display: 'none' }} onChange={handleLogoPick}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Core brand colors */}
            <Box sx={cardSx}>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', mb: 0.5 }}>Brand colors</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                    Applied across your workspace. Leave empty to use the DQ default.
                </Typography>
                <ColorGrid fields={CORE_COLOR_FIELDS} colors={colors} readOnly={readOnly} onChange={handleColorChange} />
            </Box>

            {/* Extra / global colors */}
            <Box sx={cardSx}>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', mb: 0.5 }}>Extra colors</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                    Used by dashboard buttons and custom styles (global_1–3 CSS variables).
                </Typography>
                <ColorGrid fields={EXTRA_COLOR_FIELDS} colors={colors} readOnly={readOnly} onChange={handleColorChange} />
            </Box>

            {/* Fonts */}
            <Box sx={cardSx}>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', mb: 0.5 }}>Fonts</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                    Upload a font file or paste a font URL.
                </Typography>
                {FONT_FIELDS.map(({ key, label, help }) => (
                    <Box key={key} sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 0.75 }}>{label}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <TextField
                                size="small" fullWidth disabled={readOnly || !!fontUploads[key]}
                                placeholder="https://…"
                                value={fontUploads[key] ? fontUploads[key].name : fontLinks[key]}
                                onChange={(e) => handleFontLinkChange(key, e.target.value)}
                                sx={{ maxWidth: 420 }}
                            />
                            {!readOnly && (
                                <>
                                    <Button
                                        size="small" variant="outlined" sx={{ textTransform: 'none' }}
                                        onClick={() => fontFileInputRefs.current[key] && fontFileInputRefs.current[key].click()}
                                    >
                                        Upload file
                                    </Button>
                                    {(fontLinks[key] || fontUploads[key]) && (
                                        <Button
                                            size="small" sx={{ textTransform: 'none' }}
                                            onClick={() => {
                                                setFontLinks((p) => ({ ...p, [key]: '' }));
                                                setFontUploads((p) => ({ ...p, [key]: null }));
                                                markDirty();
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                    <input
                                        ref={(el) => { fontFileInputRefs.current[key] = el; }}
                                        type="file" style={{ display: 'none' }}
                                        accept=".woff,.woff2,.ttf,.otf,font/*"
                                        onChange={(e) => handleFontFile(key, e)}
                                    />
                                </>
                            )}
                        </Box>
                        {fontUploads[key] && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#059669', mt: 0.5 }}>
                                File attached — click Save appearance to upload.
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mt: 0.5 }}>{help}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Custom stylesheet */}
            <Box sx={cardSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>Custom CSS</Typography>
                    {!readOnly && (
                        <Box>
                            <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                                    onClick={() => cssFileInputRef.current && cssFileInputRef.current.click()}>
                                Upload .css file
                            </Button>
                            <input ref={cssFileInputRef} type="file" accept=".css,text/css" style={{ display: 'none' }} onChange={handleStylesheetFile} />
                        </Box>
                    )}
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 1.5, mt: 0.5 }}>
                    Upload a .css file or paste a stylesheet URL. Preview only — edit the file and re-upload to change.
                </Typography>
                {!readOnly && (
                    <TextField
                        size="small" fullWidth disabled={!!stylesheetUpload}
                        placeholder="https://…/styles.css"
                        value={stylesheetUpload ? stylesheetUpload.name : stylesheetLink}
                        onChange={(e) => {
                            markDirty();
                            setStylesheetLink(e.target.value);
                            setStylesheetUpload(null);
                        }}
                        sx={{ mb: 2, maxWidth: 520 }}
                    />
                )}
                <Box sx={{
                    border: '1px solid #E5E7EB', borderRadius: '8px', p: 2,
                    backgroundColor: '#F9FAFB', maxHeight: 240, overflow: 'auto',
                    fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap',
                }}>
                    {stylesheetPreview || '(No stylesheet)'}
                </Box>
            </Box>

            {!readOnly && (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="contained" disableElevation disabled={saving} onClick={handleSave}
                        startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={primaryBtnSx}
                    >
                        {saving ? 'Saving…' : 'Save appearance'}
                    </Button>
                    <Button variant="outlined" disabled={saving} onClick={() => setResetDialogOpen(true)} sx={{ textTransform: 'none' }}>
                        Reset to default
                    </Button>
                </Box>
            )}

            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle>Reset appearance?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.9rem', color: '#4B5563' }}>
                        This will remove all custom branding for this workspace and restore the default DQ appearance.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleResetConfirm} color="error" sx={{ textTransform: 'none' }}>Reset</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AppearanceTab;
